import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getStaleTime, QUERY_TYPES } from '@/config/reactQuery'
import { useAtom } from 'jotai'
import { useEffect } from 'react'
import SettingsService from '@/libs/services/settings'
import { dirtyModelAtom, logoFileAtom } from './organizationAtoms'

const ORGANIZATION_QUERY_KEY = ['organization']

const fetchOrganization = async () => {
  const data = await SettingsService.getOrganization()
  return data
}

/**
 * Custom React hook to manage organization information.
 * ------------------------------------------------------------------
 * Quick usage:
 *
 * const {
 *   organization,      // Current organization data (or undefined if not yet exists)
 *   dirtyModel,        // Local editable state (copy of organization that user can modify)
 *   setDirtyModel,     // Setter to update dirtyModel (e.g. from forms)
 *   logoFile,          // Pending logo file to upload (File | null)
 *   setLogoFile,       // Setter for logoFile
 *   submitOrganization,// Async function that creates/updates organization with dirtyModel
 *   isLoading,         // Loading initial data
 *   isError,           // Error getting data
 *   error,             // Error object if isError
 *   refetch,           // Force data refetch
 * } = useOrganization();
 *
 * Typical form pattern:
 * -------------------------------------------------
 * 1. When component mounts, organization data is automatically fetched.
 * 2. Initialize your form with `organization?.data` (or default values).
 * 3. In form's onFinish:
 *      setDirtyModel(values);
 *      await submitOrganization();
 * 4. The hook will internally decide whether to create or update the organization.
 *
 * Options:
 *   useOrganization({
 *     notFoundErrorHandler: (err) => { /* handle 404 error *\/ }
 *   });
 *
 * submitOrganization({
 *   badRequestHandler: (err) => { /* handle 400 *\/ },
 *   model: values // optional, to force the body to send
 * });
 */

const useOrganization = ({ notFoundErrorHandler } = {}) => {
  const queryClient = useQueryClient()
  const [dirtyModel, setDirtyModel] = useAtom(dirtyModelAtom)
  const [logoFile, setLogoFile] = useAtom(logoFileAtom)

  // Query para obtener la organización
  const {
    data: organization,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ORGANIZATION_QUERY_KEY,
    queryFn: fetchOrganization,
    retry: false,
    onError: err => {
      if (err?.response?.status === 404 && notFoundErrorHandler) {
        notFoundErrorHandler(err)
      }
    },
    staleTime: getStaleTime(QUERY_TYPES.ORGANIZATION),
  })

  // Sincroniza dirtyModel con la organización obtenida
  useEffect(() => {
    if (organization) setDirtyModel({ ...organization.data })
  }, [organization, setDirtyModel])

  // Mutación para crear/actualizar organización
  const mutation = useMutation({
    mutationFn: async ({ body, logoFile, isUpdate, id }) => {
      if (isUpdate) {
        return SettingsService.updateOrganization(id, body, logoFile)
      }
      return SettingsService.createOrganization({ body, logoFile })
    },
    onSuccess: data => {
      queryClient.setQueryData(ORGANIZATION_QUERY_KEY, data)
      setDirtyModel({ ...data })
      setLogoFile(null)
    },
  })

  // Submit handler
  const submitOrganization = async ({ badRequestHandler, model } = {}) => {
    // Determinar si la operación es de actualización o creación
    const isUpdate = !!organization?.data?.id

    // Si no hay cambios y no hay logo nuevo, evita la llamada a la API
    const currentModel = model || dirtyModel

    if (isUpdate && JSON.stringify(organization) === JSON.stringify(currentModel) && !logoFile) {
      return organization
    }

    // Elimina campos nulos o vacíos para no enviarlos al backend
    const body = Object.fromEntries(
      Object.entries(currentModel).filter(([_, v]) => v != null && v !== '')
    )
    delete body.logo

    try {
      await mutation.mutateAsync({
        body,
        logoFile,
        isUpdate,
        id: organization?.data?.id,
      })
    } catch (err) {
      if (err?.response?.status === 400 && badRequestHandler) {
        badRequestHandler(err)
      } else {
        throw err
      }
    }
  }

  return {
    dirtyModel,
    setDirtyModel,
    logoFile,
    setLogoFile,
    submitOrganization,
    organization,
    isLoading,
    isError,
    error,
    refetch,
    mutation,
  }
}

export default useOrganization
